import produce from "immer";
import { memoize, noop } from "lodash";
import type { StateCreator } from "zustand";
import { defaultBlocks } from "@circle-react-shared/BuilderUI/DefaultBlocks";
import {
  createBlockEditorMapping,
  createBlockMetaMapping,
  createBlockRendererMapping,
} from "@circle-react-shared/BuilderUI/Helpers/blockMappings";
import { isComponentExport } from "@circle-react-shared/BuilderUI/Helpers/typePredicates";
import type { Middlewares, Mutators } from "./index";
import type { BlockRegistryState, BuilderUIState } from "./types";

export const createBlockRegistrySlice: StateCreator<
  BuilderUIState,
  Middlewares,
  Mutators,
  BlockRegistryState
> = (_set, get) => {
  const memoizedGetBlockEditors = memoize(() =>
    createBlockEditorMapping(get().blockMapping),
  );

  const memoizedGetBlockRenderers = memoize(() =>
    createBlockRendererMapping(get().blockMapping),
  );

  const memoizedGetBlockMetaMapping = memoize(() =>
    createBlockMetaMapping(get().blockMapping),
  );

  return {
    blockMapping: defaultBlocks,

    onSave: noop,

    getBlockEditors: memoizedGetBlockEditors,
    getBlockRenderers: memoizedGetBlockRenderers,
    getBlockMetaMapping: memoizedGetBlockMetaMapping,

    getBlockMeta: (blockType: string) => {
      const blockMetaMapping = memoizedGetBlockMetaMapping();
      return blockMetaMapping[blockType] ?? null;
    },

    resetBlockStyle: () => {
      const { getSelectedBlock, updateSelectedBlock, blockMapping } = get();
      const selectedBlockData = getSelectedBlock();
      if (!selectedBlockData?.block || !selectedBlockData?.meta) return;

      const { block } = selectedBlockData;
      const blockExport = blockMapping[block.type];
      if (!isComponentExport(blockExport)) return;

      if (!blockExport.defaultData.props?.styles) return;

      const updatedBlock = produce(block, draft => {
        draft.props.styles = structuredClone(
          blockExport.defaultData.props.styles,
        );
      });

      updateSelectedBlock(updatedBlock);
    },
  };
};
